import { createRixRepl, findHelp } from "./repl-runtime.js";
import { pluginProfileFromUrl, stripMarkedPluginProfile } from "./plugin-profile.js";
import { mountOutputWidgets } from "../../rix/src/index.js";
import { IntervalExplorer, isRationalIntervalValue } from "./interval-explorer.js";
import { ReactiveDashboard } from "./reactive-dashboard.js";
import { findShowcaseExamples, showcaseExample } from "./showcase-examples.js";
import { createNewGeometryBoard } from "./geometry-board.js";
import { isInteractiveOutputEvent } from "./interactive-output.js";
import {
    ClearCoordinator,
    createSessionSnapshot,
    modePresentation,
    parseSession,
    serializePortableSession,
    serializeSession,
} from "./workspace-state.js";

const repl = createRixRepl({ pluginProfile: pluginProfileFromUrl(window.location.href) });
const outputHistory = document.querySelector("#output-history");
const input = document.querySelector("#calculator-input");
const completionGhost = document.querySelector("#completion-ghost");
const completionHint = document.querySelector("#completion-hint");
const calculator = document.querySelector(".calculator");
const scriptToggle = document.querySelector("#script-toggle");
const lineSeparatorToggle = document.querySelector("#line-separator-toggle");
const clearButton = document.querySelector("#clear-button");
const scriptNote = document.querySelector("#script-note");
const sessionStatus = document.querySelector("#session-status");
const helpDialog = document.querySelector("#help-dialog");
const helpSearch = document.querySelector("#help-search");
const helpContent = document.querySelector("#help-content");
const fileInput = document.querySelector("#file-input");
const inspectDialog = document.querySelector("#inspect-dialog");
const inspectSource = document.querySelector("#inspect-source");
const inspectValue = document.querySelector("#inspect-value");
const numberDialog = document.querySelector("#number-dialog");
const numberForm = document.querySelector("#number-form");
const numberInputBase = document.querySelector("#number-input-base");
const numberDisplayProfile = document.querySelector("#number-display-profile");
const numberPersist = document.querySelector("#number-persist");
const numberSettingsStatus = document.querySelector("#number-settings-status");
const intervalDialog = document.querySelector("#interval-dialog");
const mobileCommandPanel = document.querySelector("#mobile-command-panel");
const reactiveDashboardPanel = document.querySelector("#reactive-dashboard-panel");
const reactiveDashboardToggle = document.querySelector("#reactive-dashboard-toggle");
const NUMBER_STORAGE_KEY = "ratcalc.number-config.v1";
const REACTIVE_DASHBOARD_EXAMPLE = `$$width := .Slider(3, 0:10, 1/2, "Width");
$$height := .Slider(2, 0:10, 1/2, "Height");
$$area := $width * $height;
$area`;

let scriptMode = false;
let history = [];
let historyIndex = -1;
let transcript = [];
let autoSeparateLines = true;
let completionState = null;
let clearTimer = null;
let geometryBoardSequence = 0;
const outputDisposers = new Set();
const clearCoordinator = new ClearCoordinator();
const intervalExplorer = new IntervalExplorer({
    dialog: intervalDialog,
    evaluate: (source) => repl.run(source),
    inspect: (source) => repl.readExactLeaf(source),
    onUse: (source) => setInput(source),
});
const reactiveDashboard = new ReactiveDashboard({
    panel: reactiveDashboardPanel,
    toggle: reactiveDashboardToggle,
    repl,
    onUse: (source) => setInput(source),
    onLoadExample: () => {
        setScriptMode(true);
        setInput(REACTIVE_DASHBOARD_EXAMPLE);
    },
});

function escapeHtml(value) {
    return String(value).replace(/[&<>'"]/g, (character) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#039;", '"': "&quot;",
    })[character]);
}

function scrollTranscript() {
    requestAnimationFrame(() => { outputHistory.scrollTop = outputHistory.scrollHeight; });
}

function setInput(value) {
    clearCompletion();
    input.value = value;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
    input.focus();
}

function insertInputText(text) {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const value = `${input.value.slice(0, start)}${text}${input.value.slice(end)}`;
    setInput(value);
    const cursor = start + text.length;
    input.selectionStart = input.selectionEnd = cursor;
}

function clearCompletion() {
    completionState = null;
    completionGhost.replaceChildren();
    completionHint.hidden = true;
    completionHint.replaceChildren();
}

function renderCompletion() {
    if (!completionState) return;
    const candidate = completionState.result.candidates[completionState.index];
    const { from, to } = completionState.result;
    const typed = input.value.slice(from, to);
    const startsWithTyped = candidate.insertText.toLowerCase().startsWith(typed.toLowerCase());
    const suffix = startsWithTyped ? candidate.insertText.slice(typed.length) : "";
    completionGhost.replaceChildren(document.createTextNode(input.value.slice(0, to)), Object.assign(document.createElement("span"), { className: "suffix", textContent: suffix }));
    completionGhost.scrollTop = input.scrollTop;
    completionHint.hidden = false;
    completionHint.innerHTML = `<b>${escapeHtml(candidate.insertText)}</b> · ${escapeHtml(candidate.detail)}${candidate.preview ? ` — ${escapeHtml(candidate.preview)}` : ""}`;
}

function beginCompletion() {
    if (input.selectionStart !== input.selectionEnd) return clearCompletion();
    const result = repl.complete(input.value, input.selectionStart);
    if (!result.candidates.length) return clearCompletion();
    completionState = { result, index: 0 };
    renderCompletion();
}

function moveCompletion(direction) {
    if (!completionState) return false;
    const { candidates } = completionState.result;
    completionState.index = (completionState.index + direction + candidates.length) % candidates.length;
    renderCompletion();
    return true;
}

function acceptCompletion() {
    if (!completionState) return false;
    const { from, to, candidates } = completionState.result;
    const candidate = candidates[completionState.index];
    const value = `${input.value.slice(0, from)}${candidate.insertText}${input.value.slice(to)}`;
    const cursor = from + candidate.insertText.length;
    clearCompletion();
    input.value = value;
    input.selectionStart = input.selectionEnd = cursor;
    setInput(value);
    return true;
}

function appendOutput(source, response) {
    const entry = document.createElement("article");
    entry.className = "output-entry";
    const sourceLine = document.createElement("div");
    sourceLine.className = "input-line";
    sourceLine.innerHTML = `<span class="prompt">&gt;</span>${escapeHtml(source)}<span class="reload-icon" title="Reload expression">↻</span>`;
    sourceLine.addEventListener("click", () => setInput(source));
    entry.appendChild(sourceLine);

    if (response.type === "help") {
        entry.appendChild(inlineHelp(response));
    } else {
        const outputLine = document.createElement("div");
        const inspectable = Boolean(response.html) || response.text.includes("\n");
        const preview = inspectable ? `${response.text.split("\n")[0]}\n… inspect full result` : response.text;
        outputLine.className = `${response.type === "error" ? "error-line" : "output-line"}${inspectable ? " truncated" : ""}`;
        if (response.html) {
            outputLine.classList.add("rich-output");
            outputLine.innerHTML = response.html;
            outputLine.addEventListener("click", (event) => {
                if (!isInteractiveOutputEvent(event, outputLine)) openInspection(source, response.text);
            });
            const disposeWidgets = mountOutputWidgets(outputLine, response.value, {
                format: repl.formatValue,
                observe: response.observe
                    ? (listener) => response.observe((next) => listener(next.value))
                    : null,
                onActivate: ({ address }) => insertInputText(address),
                evaluateEdit: (editSource, { mode }) => repl.run(mode === "formula"
                    ? `@{ ${editSource} }`
                    : editSource),
            });
            const dispose = () => {
                disposeWidgets();
                response.dispose?.();
            };
            outputDisposers.add(dispose);
        } else {
            outputLine.innerHTML = response.type === "error"
                ? escapeHtml(preview)
                : `${escapeHtml(preview)}<span class="inject-icon" title="Use this value">→</span>`;
            if (inspectable) outputLine.addEventListener("click", () => openInspection(source, response.text));
            else if (response.type === "result") outputLine.addEventListener("click", () => setInput(response.sourceText ?? response.text));
            if (response.type === "result" && isRationalIntervalValue(response.value)) {
                const explore = document.createElement("button");
                explore.type = "button";
                explore.className = "interval-explore-button";
                explore.textContent = "Explore exact value";
                explore.addEventListener("click", (event) => {
                    event.stopPropagation();
                    intervalExplorer.open(source, response.value, response.exactTrace);
                });
                outputLine.appendChild(explore);
            }
        }
        entry.appendChild(outputLine);
    }
    outputHistory.appendChild(entry);
    transcript.push({ source, text: response.type === "help" ? `.Help: ${response.query || "all topics"}` : response.text });
    scrollTranscript();
}

function openNumberSettings() {
    const config = repl.numberConfig();
    numberInputBase.value = config.input;
    numberDisplayProfile.value = config.display;
    numberSettingsStatus.textContent = "";
    numberDialog.showModal();
    numberInputBase.focus();
}

function saveNumberSettings(config) {
    if (numberPersist.checked) localStorage.setItem(NUMBER_STORAGE_KEY, JSON.stringify(config));
    else localStorage.removeItem(NUMBER_STORAGE_KEY);
}

function applyNumberSettings(config, { close = false } = {}) {
    try {
        const applied = repl.setNumberConfig(config);
        numberInputBase.value = applied.input;
        numberDisplayProfile.value = applied.display;
        saveNumberSettings(applied);
        numberSettingsStatus.textContent = `Using #${applied.input} input and ${applied.display} output.`;
        reactiveDashboard.refresh({ rebuildControls: false });
        if (close) numberDialog.close();
    } catch (error) {
        numberSettingsStatus.textContent = error.message || String(error);
    }
}

function setNumberPreset(profile) {
    numberDisplayProfile.value = profile;
    applyNumberSettings({ display: profile });
}

async function copySession() {
    const button = document.querySelector('[data-action="copy"]');
    const text = transcript.map((entry) => `> ${entry.source}\n${entry.text}`).join("\n\n");
    if (!text) {
        button.textContent = "Nothing to copy";
        setTimeout(() => { button.textContent = "Transcript"; }, 1600);
        return;
    }
    try {
        if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
        await navigator.clipboard.writeText(text);
        button.textContent = "Copied";
    } catch {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.className = "clipboard-fallback";
        document.body.appendChild(textarea);
        textarea.select();
        let copied = false;
        try { copied = document.execCommand("copy"); } catch { copied = false; }
        textarea.remove();
        button.textContent = copied ? "Copied" : "Copy failed";
    }
    setTimeout(() => { button.textContent = "Transcript"; }, 1600);
}

function setSessionStatus(message, { error = false } = {}) {
    sessionStatus.textContent = message;
    sessionStatus.classList.toggle("error", error);
}

function currentSessionSnapshot() {
    const reactiveInputs = repl.reactiveVariables()
        .filter(({ dependencies }) => dependencies.length === 0)
        .map(({ name, sourceText }) => ({ name, source: sourceText }));
    return createSessionSnapshot({
        transcript,
        input: input.value,
        scriptMode,
        autoSeparateLines,
        numberConfig: repl.numberConfig(),
        reactiveInputs,
        dashboardOpen: reactiveDashboard.isOpen,
        dashboardPresentation: reactiveDashboard.presentation,
        pluginProfile: repl.pluginProfile(),
    });
}

function downloadText(filename, text, type) {
    const link = document.createElement("a");
    const url = URL.createObjectURL(new Blob([text], { type }));
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

function saveSession() {
    const date = new Date().toISOString().slice(0, 10);
    downloadText(`ratcalc-${date}.rix-session`, serializeSession(currentSessionSnapshot()), "application/json");
    setSessionStatus("Session saved. Load this .rix-session file to restore it.");
}

function exportPortableSession() {
    const date = new Date().toISOString().slice(0, 10);
    downloadText(`ratcalc-${date}.rix`, serializePortableSession(currentSessionSnapshot()), "text/plain");
    setSessionStatus("Portable RiX source exported. Run it with the rix command-line runner.");
}

function restoreNumberSettings() {
    try {
        const saved = JSON.parse(localStorage.getItem(NUMBER_STORAGE_KEY) || "null");
        if (saved && typeof saved === "object") {
            numberPersist.checked = true;
            applyNumberSettings(saved);
        }
    } catch {
        localStorage.removeItem(NUMBER_STORAGE_KEY);
    }
}

function inlineHelp({ query, groups }) {
    const panel = document.createElement("section");
    panel.className = "inline-help";
    const title = query ? `Help for “${escapeHtml(query)}”` : "RiX and RatCalc help";
    const body = groups.length
        ? groups.map((group) => `<h4>${escapeHtml(group.title)}</h4><ul>${group.items.map(([syntax, description]) => `<li><code>${escapeHtml(syntax)}</code> — ${escapeHtml(description)}</li>`).join("")}</ul>`).join("")
        : `<p>No help topic matched “${escapeHtml(query)}”. Try <code>.Help("interval")</code>.</p>`;
    panel.innerHTML = `<h3>${title}</h3>${body}`;
    return panel;
}

function renderHelp(query = "") {
    const { groups } = findHelp(query);
    const examples = findShowcaseExamples(query);
    const intro = query ? "" : `<p class="help-overview">Choose a section for syntax, examples, and calculator features.</p>`;
    const exampleGroups = new Map();
    for (const example of examples) {
        const entries = exampleGroups.get(example.category) || [];
        entries.push(example);
        exampleGroups.set(example.category, entries);
    }
    const showcaseSection = examples.length ? `
        <details class="help-section help-showcases">
            <summary><span><b>Runnable examples</b><small>Load complete programs for exact arithmetic, reactive models, graphics, and polynomial work.</small></span><i>${examples.length} example${examples.length === 1 ? "" : "s"}</i></summary>
            <div class="help-section-body">
                ${[...exampleGroups].map(([category, entries]) => `
                    <section class="help-showcase-group">
                        <h4>${escapeHtml(category)}</h4>
                        <div class="help-showcase-grid">
                            ${entries.map((example) => `
                                <button type="button" class="help-showcase-card" data-showcase-example="${escapeHtml(example.id)}" aria-label="Load ${escapeHtml(example.title)}">
                                    <span class="help-showcase-meta"><i>${escapeHtml(example.complexity)}</i><i>${escapeHtml(example.output)}</i></span>
                                    <b>${escapeHtml(example.title)}</b>
                                    <small>${escapeHtml(example.summary)}</small>
                                    <code>${escapeHtml(example.source.split("\n").find((line) => line.trim())?.trim() || example.source)}</code>
                                    <span class="help-showcase-load">Load example →</span>
                                </button>
                            `).join("")}
                        </div>
                    </section>
                `).join("")}
            </div>
        </details>` : "";
    const sections = groups.length
        ? groups.map((group) => `<details class="help-section help-group"><summary><span><b>${escapeHtml(group.title)}</b><small>${escapeHtml(group.description)}</small></span><i>${group.items.length} topic${group.items.length === 1 ? "" : "s"}</i></summary><div class="help-section-body">${group.items.map(([syntax, description]) => `<div class="help-item"><code>${escapeHtml(syntax)}</code><p>${escapeHtml(description)}</p></div>`).join("")}</div></details>`).join("")
        : examples.length ? "" : `<p class="help-intro">No matching help topic or showcase. Try “interval”, “reactive”, “polynomial”, or “graphic”.</p>`;
    helpContent.innerHTML = intro + showcaseSection + sections;
}

function openHelp(query = "") {
    helpSearch.value = query;
    renderHelp(query);
    helpDialog.showModal();
    helpSearch.focus();
}

function loadShowcase(id) {
    const example = showcaseExample(id);
    if (!example) return;
    helpDialog.close();
    setScriptMode(example.source.includes("\n") || example.source.includes(";"));
    setInput(example.source);
}

function setReactiveDashboardOpen(next) {
    if (next) {
        reactiveDashboard.open();
    } else {
        reactiveDashboard.close();
    }
    document.querySelector(".container").classList.toggle("dashboard-open", next);
}

function openInspection(source, value) {
    inspectSource.textContent = source;
    inspectValue.textContent = value;
    inspectDialog.showModal();
}

async function clearSession(options = {}) {
    for (const dispose of outputDisposers) dispose();
    outputDisposers.clear();
    await repl.reset(options);
    history = [];
    historyIndex = -1;
    transcript = [];
    outputHistory.innerHTML = "";
    displayWelcome();
    reactiveDashboard.restorePresentation({});
    reactiveDashboard.refresh();
    setInput("");
    clearCoordinator.reset();
    updateClearButton();
    setSessionStatus("Session cleared.");
}

function updateClearButton() {
    const armed = clearCoordinator.armed;
    for (const button of document.querySelectorAll('[data-action="clear"]')) {
        button.textContent = armed ? "Clear history?" : (button === clearButton ? "Clear input" : "Clear");
        button.classList.toggle("danger", armed);
        button.setAttribute("aria-label", armed ? "Clear calculator history and reset the session" : "Clear current input");
    }
}

function resetClearConfirmation() {
    if (clearTimer) clearTimeout(clearTimer);
    clearTimer = null;
    clearCoordinator.reset();
    updateClearButton();
}

async function handleClear() {
    const intent = clearCoordinator.activate(Boolean(input.value));
    if (intent === "clear-session") {
        if (clearTimer) clearTimeout(clearTimer);
        clearTimer = null;
        await clearSession();
        return;
    }
    if (intent === "clear-input") setInput("");
    updateClearButton();
    setSessionStatus("Input cleared. Select “Clear history?” to reset the full session.");
    if (clearTimer) clearTimeout(clearTimer);
    clearTimer = setTimeout(resetClearConfirmation, 5000);
}

function displayWelcome() {
    const welcome = document.createElement("section");
    welcome.className = "welcome";
    welcome.innerHTML = `<b>Welcome to RatCalc!</b><br />Type a RiX expression and press Enter to calculate.<br />Use <code>.help</code> for the guide or <code>.Help("interval")</code> for inline help.`;
    outputHistory.appendChild(welcome);
}

function showVariables(source) {
    const variables = repl.variables();
    const text = variables.length
        ? `Variables:\n${variables.map(({ name, value }) => `  ${name} = ${value}`).join("\n")}`
        : "No variables or functions defined.";
    appendOutput(source, { type: "result", text });
}

async function execute(source = input.value) {
    const command = source.trim();
    if (!command) return;
    if (/^\.help(?:\s+.*)?$/i.test(command)) {
        openHelp(command.replace(/^\.help/i, "").trim());
        setInput("");
        return;
    }
    if (/^\.clear$/i.test(command)) { await clearSession(); return; }
    if (/^\.vars$/i.test(command)) { showVariables(source); setInput(""); return; }

    if (history.at(-1) !== source) history.push(source);
    historyIndex = -1;
    const response = await repl.runAsync(source);
    appendOutput(source, response);
    reactiveDashboard.refresh();
    setInput("");
}

async function openNewGeometryBoard() {
    const names = new Set(repl.variables().map(({ name }) => name.toLowerCase()));
    do geometryBoardSequence += 1;
    while (names.has(`geometryboard${geometryBoardSequence}seed`) || names.has(`geometryboard${geometryBoardSequence}graph`));
    const board = createNewGeometryBoard(geometryBoardSequence);
    setScriptMode(true);
    await execute(board.source);
    [...outputHistory.querySelectorAll(`[data-rix-graphic-action="${board.pointActionId}"]`)].at(-1)?.focus?.();
}

function setScriptMode(next) {
    scriptMode = next;
    const presentation = modePresentation(scriptMode);
    calculator.classList.toggle("script-mode", scriptMode);
    scriptToggle.classList.toggle("active", scriptMode);
    scriptToggle.setAttribute("aria-pressed", String(scriptMode));
    scriptToggle.textContent = presentation.buttonLabel;
    scriptNote.hidden = !scriptMode;
    document.querySelector("#entry-mode-label").textContent = presentation.status;
    input.placeholder = `${presentation.placeholder} — .help for help`;
    setInput(input.value);
}

function setAutoSeparateLines(next) {
    autoSeparateLines = next;
    repl.setAutoSeparateLines(autoSeparateLines);
    lineSeparatorToggle.checked = autoSeparateLines;
}

function continueCommand() {
    const position = input.selectionStart;
    const before = input.value.slice(0, position);
    const after = input.value.slice(position);
    const slash = before.lastIndexOf("\\");
    input.value = `${before.slice(0, slash)}\n${after}`;
    input.selectionStart = input.selectionEnd = slash + 1;
    setInput(input.value);
}

function navigateHistory(direction) {
    if (scriptMode || history.length === 0) return;
    if (direction < 0) historyIndex = historyIndex < 0 ? history.length - 1 : Math.max(0, historyIndex - 1);
    else historyIndex = historyIndex >= history.length - 1 ? -1 : historyIndex + 1;
    setInput(historyIndex < 0 ? "" : history[historyIndex]);
}

async function loadFile(file) {
    const text = await file.text();
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith(".rix-session")) {
        try {
            await restoreSession(parseSession(text));
            setSessionStatus(`Restored ${file.name}.`);
        } catch (error) {
            setSessionStatus(error.message || String(error), { error: true });
        }
    } else if (lowerName.endsWith(".js")) {
        appendOutput(`.load ${file.name}`, { type: "result", text: "JavaScript module selected. Browser execution is intentionally held behind a future trust policy." });
    } else {
        setScriptMode(true);
        setInput(stripMarkedPluginProfile(text, repl.pluginProfile().source));
    }
}

async function restoreSession(session) {
    await clearSession(session.pluginProfile ? { pluginProfile: session.pluginProfile } : {});
    setAutoSeparateLines(session.autoSeparateLines);
    applyNumberSettings(session.numberConfig);
    for (const entry of session.transcript) {
        if (/^\.vars$/i.test(entry.source.trim())) {
            showVariables(entry.source);
            continue;
        }
        const response = await repl.runAsync(entry.source);
        if (history.at(-1) !== entry.source) history.push(entry.source);
        appendOutput(entry.source, response);
    }
    for (const reactive of session.reactiveInputs) {
        const response = await repl.runAsync(`$${reactive.name} := ${reactive.source}`);
        if (response.type === "error") throw new Error(`Could not restore reactive input ${reactive.name}: ${response.text}`);
    }
    reactiveDashboard.restorePresentation(session.dashboardPresentation);
    reactiveDashboard.refresh();
    setScriptMode(session.scriptMode);
    setInput(session.input);
    setReactiveDashboardOpen(session.dashboardOpen);
}

document.addEventListener("click", (event) => {
    const control = event.target.closest("[data-action]");
    if (!control) return;
    switch (control.dataset.action) {
    case "run": execute(); break;
    case "help": openHelp(); break;
    case "number-settings": openNumberSettings(); break;
    case "close-number-settings": numberDialog.close(); input.focus(); break;
    case "reset-number-settings": numberInputBase.value = "z[10]"; numberDisplayProfile.value = ".."; applyNumberSettings({ input: "z[10]", display: ".." }); break;
    case "close-help": helpDialog.close(); input.focus(); break;
    case "reactive-dashboard": setReactiveDashboardOpen(!reactiveDashboard.isOpen); break;
    case "new-geometry-board": void openNewGeometryBoard(); break;
    case "close-reactive-dashboard": setReactiveDashboardOpen(false); input.focus(); break;
    case "refresh-reactive-dashboard": reactiveDashboard.refresh(); break;
    case "close-inspect": inspectDialog.close(); input.focus(); break;
    case "close-interval": intervalExplorer.close(); input.focus(); break;
    case "clear": void handleClear(); break;
    case "script": setScriptMode(!scriptMode); break;
    case "copy": void copySession(); break;
    case "save": saveSession(); break;
    case "export-rix": exportPortableSession(); break;
    case "load": fileInput.click(); break;
    case "interval-export-svg": intervalExplorer.download("svg"); break;
    case "interval-export-html": intervalExplorer.download("html"); break;
    case "interval-export-text": intervalExplorer.download("txt"); break;
    case "interval-use": intervalExplorer.useResult(); break;
    default: break;
    }
});

document.addEventListener("click", (event) => {
    const showcase = event.target.closest("[data-showcase-example]");
    if (showcase) {
        loadShowcase(showcase.dataset.showcaseExample);
        return;
    }
    const preset = event.target.closest("[data-number-preset]");
    if (preset) {
        setNumberPreset(preset.dataset.numberPreset);
        if (preset.closest("#mobile-command-panel")) mobileCommandPanel.hidden = true;
    }
    const insert = event.target.closest("[data-insert]");
    if (insert) insertInputText(insert.dataset.insert);
    const mobileAction = event.target.closest("[data-mobile-action]")?.dataset.mobileAction;
    if (mobileAction === "backspace") {
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? start;
        if (start !== end) {
            input.setRangeText("", start, end, "end");
        } else if (start > 0) {
            input.setRangeText("", start - 1, start, "end");
        }
        setInput(input.value);
    }
    if (mobileAction === "clear-input") setInput("");
    if (mobileAction === "commands") mobileCommandPanel.hidden = !mobileCommandPanel.hidden;
    const mobileCommand = event.target.closest("[data-mobile-command]")?.dataset.mobileCommand;
    if (mobileCommand) {
        mobileCommandPanel.hidden = true;
        void execute(mobileCommand);
    }
});

input.addEventListener("input", () => { resetClearConfirmation(); setInput(input.value); });
input.addEventListener("scroll", () => { if (completionState) renderCompletion(); });
input.addEventListener("keydown", (event) => {
    if (event.key === "Tab") { event.preventDefault(); if (!acceptCompletion()) beginCompletion(); return; }
    if (event.key === "ArrowUp" && moveCompletion(-1)) { event.preventDefault(); return; }
    if (event.key === "ArrowDown" && moveCompletion(1)) { event.preventDefault(); return; }
    if (event.key === "ArrowRight" && acceptCompletion()) { event.preventDefault(); return; }
    if ((event.key === "ArrowLeft" || event.key === "Escape") && completionState) { event.preventDefault(); clearCompletion(); return; }
    // Keep ordinary arrows available for moving through a multiline textarea.
    // Shift+ArrowUp/Down are deliberate mode changes rather than history navigation.
    if (event.shiftKey && event.key === "ArrowUp") {
        event.preventDefault();
        setScriptMode(true);
        return;
    }
    if (event.shiftKey && event.key === "ArrowDown") {
        event.preventDefault();
        setScriptMode(false);
        return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") { event.preventDefault(); execute(); return; }
    if (event.key === "Enter" && !scriptMode) {
        event.preventDefault();
        const currentLine = input.value.slice(0, input.selectionStart).split("\n").at(-1).trimEnd();
        if (currentLine.endsWith("\\")) continueCommand();
        else execute();
        return;
    }
    if (event.key === "ArrowUp" && input.selectionStart === 0) { event.preventDefault(); navigateHistory(-1); }
    if (event.key === "ArrowDown" && input.selectionStart === input.value.length) { event.preventDefault(); navigateHistory(1); }
});
helpSearch.addEventListener("input", () => renderHelp(helpSearch.value));
fileInput.addEventListener("change", async () => { const [file] = fileInput.files; if (file) await loadFile(file); fileInput.value = ""; });
numberForm.addEventListener("submit", (event) => {
    event.preventDefault();
    setAutoSeparateLines(lineSeparatorToggle.checked);
    applyNumberSettings({ input: numberInputBase.value, display: numberDisplayProfile.value }, { close: true });
});
[helpDialog, inspectDialog, numberDialog, intervalDialog].forEach((dialog) => {
    dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
    });
});

displayWelcome();
setAutoSeparateLines(autoSeparateLines);
restoreNumberSettings();
reactiveDashboard.refresh();
if (repl.pluginProfile().warnings.length) {
    setSessionStatus(repl.pluginProfile().warnings.join(" "), { error: true });
}
input.focus();
window.addEventListener("pagehide", () => { reactiveDashboard.dispose(); void repl.dispose(); });
